import { useEffect, useCallback, useMemo, useRef } from 'react';
import { Box, Flex, Text, Spinner, useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { updateMovement } from '../../api/endpointsBackend';
import { useMovementsData } from '../../hooks/useMovementsData';
import { groupMovementsByDate } from '../../utils/groupMovementsByDate';
import LoadMoreButton from '../movements/LoadMoreButton';
import CompactMovementRow from './CompactMovementRow';
import { ConvertedAmountsProvider, useDisplayAmount } from '../../contexts/ConvertedAmountsContext';
import InlineCategoryPicker from './InlineCategoryPicker';
import InlineTagPicker from './InlineTagPicker';
import { dayShortUpper, monthLong, monthLongUpper } from '../../utils/intlFormatters';
import { transferDirection } from '../../utils/movementDisplay';
import { tradeLabel } from '../../utils/subAccounts';

// A row's amount in the display currency, from the provider around the list,
// as on the Movements page: `≈` when converted, dimmed until the value lands.
function ConvertedRow({ movement, ...rowProps }) {
  const shown = useDisplayAmount(movement);
  return (
    <CompactMovementRow
      {...rowProps}
      amount={shown.amount}
      currency={shown.currency}
      approx={shown.isConverted}
      pending={shown.isPending}
      rateSources={shown.rateSources}
    />
  );
}

/**
 * Reusable movements list for detail panels (Categories, Accounts) — same UX
 * as the Dashboard "Incomplete movements" card: inline category/tag pickers on
 * every row, click-to-detail via `onMovementClick`.
 *
 * Pass `filters` in the shape the backend expects (e.g. `account_id in [id]`
 * or `category_id in [id]`); currency is appended automatically.
 *
 * MI-245 — it reads through the Movements list's row window: the newest 50,
 * then "Show more" with how many of the whole are on screen. It used to stop
 * at the 50 newest days without saying so.
 *
 * `refreshKey` re-reads every row on screen, keeping them there while it does.
 *
 * `shouldKeepAfterCategoryChange(movement)` lets Categories drop rows whose
 * category no longer matches the filter. Default: keep everything.
 */
export default function MovementsList({
  filters,
  dateRange,
  currency,
  categories,
  allTags,
  onMovementClick,
  refreshKey,
  emptyLabel,
  showAccountName = false,
  contextAccountId,
  shouldKeepAfterCategoryChange,
  onCategoryChanged,
}) {
  const { t } = useTranslation('movements');
  const toast = useToast();

  const effectiveFilters = useMemo(() => {
    if (!filters || filters.length === 0) return null;
    if (!currency || currency === 'all') return filters;
    return [...filters, { entity: 'Movement', column: 'currency', operator: 'in', values: [currency] }];
  }, [filters, currency]);

  const {
    movementsList, loading, loadingMore, error, paginationInfo,
    hasMore, loadMore, refetch, patchMovements,
  } = useMovementsData({ dateRange, filters: effectiveFilters, enabled: Boolean(effectiveFilters) });

  // Only a change of `refreshKey` re-reads: the hook already reads the first
  // window on mount, and StrictMode runs this effect twice there.
  const lastRefreshKey = useRef(refreshKey);
  useEffect(() => {
    if (lastRefreshKey.current === refreshKey) return;
    lastRefreshKey.current = refreshKey;
    refetch({ quiet: true });
  }, [refreshKey, refetch]);

  useEffect(() => {
    if (error) toast({ title: t('shared.movementsList.loadFailed'), status: 'error', duration: 3000 });
  }, [error, toast, t]);

  const handleCategoryChange = useCallback(async (movementId, category) => {
    try {
      await updateMovement(movementId, { category_id: category.id });
      patchMovements([movementId], { category, category_id: category.id });
      toast({ title: t('list.toasts.categoryUpdated', { name: category.name }), status: 'success', duration: 2000 });
      if (onCategoryChanged) onCategoryChanged();
    } catch (err) {
      console.error('Failed to update category:', err);
      toast({ title: t('list.toasts.categoryUpdateFailed'), status: 'error', duration: 3000 });
    }
  }, [toast, patchMovements, onCategoryChanged, t]);

  const handleTagsChange = useCallback(async (movementId, newTagIds, tags) => {
    try {
      await updateMovement(movementId, { tag_ids: newTagIds });
      const newTagMovements = newTagIds.map(id => {
        const tag = tags.find(t => t.id === id);
        return { tag_id: id, movement_id: movementId, tag };
      });
      patchMovements([movementId], { tag_movements: newTagMovements });
    } catch (err) {
      console.error('Failed to update tags:', err);
      toast({ title: t('list.toasts.tagsUpdateFailed'), status: 'error', duration: 3000 });
    }
  }, [toast, patchMovements, t]);

  const movements = shouldKeepAfterCategoryChange
    ? movementsList.filter(shouldKeepAfterCategoryChange)
    : movementsList;
  // A row an edit moved out of the list has left the whole too.
  const dropped = movementsList.length - movements.length;

  if (loading) return <Flex justify="center" py={4}><Spinner size="sm" /></Flex>;

  if (movements.length === 0) {
    return (
      <Text fontSize="sm" color="redesign.text.muted" py={6} textAlign="center">
        {emptyLabel ? t('shared.movementsList.emptyWithLabel', { label: emptyLabel }) : t('shared.movementsList.emptyDefault')}
      </Text>
    );
  }

  const groups = groupMovementsByDate(movements, 'month');
  // Copilot's list: the month, then a heading for each day, so the rows carry
  // no date of their own. The rows arrive newest first, a day's together.
  const byDay = (items) => items.reduce((days, m) => {
    const last = days[days.length - 1];
    if (last && last.date === m.date) last.items.push(m);
    else days.push({ date: m.date, items: [m] });
    return days;
  }, []);

  return (
    <ConvertedAmountsProvider>
      {groups.map((group, idx) => (
        <Box key={group.key} py={4}>
          {/* Copilot's month title: the month in full, in ink, a step up. */}
          <Text fontSize="20px" fontWeight={600} lineHeight="26px" color="redesign.heading" mb={4}>
            {(() => {
              const d = new Date(group.items[0].date + 'T00:00:00');
              return `${monthLong(d.getMonth())} ${d.getFullYear()}`;
            })()}
          </Text>
          {byDay(group.items).map(day => (
          <Box key={day.date}>
          <Text fontSize="12px" fontWeight={700} lineHeight="16px" letterSpacing="0.02em" color="redesign.text.muted" mt="13px" mb="7px">
            {(() => {
              const d = new Date(day.date + 'T00:00:00');
              return `${dayShortUpper(d.getDay())}, ${monthLongUpper(d.getMonth())} ${d.getDate()}`;
            })()}
          </Text>
          {day.items.map(m => {
            const isTransfer = m.type === 'Transfer';
            const fromName = m.from_account?.name;
            const toName = m.to_account?.name;
            const direction = transferDirection(m, contextAccountId);
            const accountSlot = isTransfer && direction ? (
              // Under one of its own accounts, only the other leg is new — and
              // the arrow says which way the money went.
              <Text fontSize="xs" isTruncated title={direction === 'out' ? toName : fromName}>
                {direction === 'out' ? `→ ${toName || t('fallback.accountUnassigned')}` : `← ${fromName || t('fallback.accountUnassigned')}`}
              </Text>
            ) : isTransfer ? (
              <Flex align="center" gap={1} minW={0} title={`${fromName || t('fallback.accountUnassigned')} → ${toName || t('fallback.accountUnassigned')}`}>
                <Text
                  fontSize="xs"
                  isTruncated
                  flex={1}
                  minW={0}
                  fontStyle={fromName ? 'normal' : 'italic'}
                  color={fromName ? undefined : 'redesign.text.faint'}
                >
                  {fromName || '—'}
                </Text>
                <Text fontSize="xs" flexShrink={0}>→</Text>
                <Text
                  fontSize="xs"
                  isTruncated
                  flex={1}
                  minW={0}
                  fontStyle={toName ? 'normal' : 'italic'}
                  color={toName ? undefined : 'redesign.text.faint'}
                >
                  {toName || '—'}
                </Text>
              </Flex>
            ) : (m.account?.name || '');
            const noteRestricted =
              (m.has_note || m.has_description) && !(m.note || m.description);
            // MI-315 — an ignored movement is listed, dimmed and struck through
            // like in the Movements list: it counts toward no total.
            const inert = m.is_ignored ? 'none' : undefined;
            return (
              <Box key={m.id} py="2px" opacity={m.is_ignored ? 0.55 : 1}>
                <ConvertedRow
                  movement={m}
                  note={m.note || m.description}
                  subNote={tradeLabel(m)}
                  noteRestricted={noteRestricted}
                  struck={Boolean(m.is_ignored)}
                  restrictedLabel={t('list.fallback.restricted')}
                  restrictedTooltip={t('list.tooltips.restricted')}
                  accountName={showAccountName || isTransfer ? accountSlot : undefined}
                  tags={allTags ? <Box pointerEvents={inert}><InlineTagPicker movement={m} allTags={allTags} onTagsChange={handleTagsChange} /></Box> : undefined}
                  category={categories ? <Box pointerEvents={inert}><InlineCategoryPicker movement={m} categories={categories} onCategoryChange={handleCategoryChange} maxW="100%" /></Box> : undefined}
                  refund={m.amount < 0 || m.type === 'Income' || direction === 'in'}
                  onClick={onMovementClick ? () => onMovementClick(m.id) : undefined}
                  size="md"
                />
              </Box>
            );
          })}
          </Box>
          ))}
        </Box>
      ))}
      {/* The Movements list's own "more", with how much of the whole is here. */}
      {hasMore && (
        <Box pb={4} pt={1}>
          <LoadMoreButton
            onClick={loadMore}
            isLoading={loadingMore}
            label={t('list.pagination.loadMore')}
            hint={paginationInfo.totalInRange
              ? t('list.pagination.showing', {
                  shown: movements.length,
                  total: paginationInfo.totalInRange - dropped,
                })
              : null}
          />
        </Box>
      )}
    </ConvertedAmountsProvider>
  );
}
